import {FC, Fragment, useState} from 'react';
import {
    Alert, Chip, Collapse, IconButton,
    Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import {KeyboardArrowDown, KeyboardArrowRight} from '@mui/icons-material';
import {AdminBatchJobType, AdminBatchSubmission, ExecutionMode} from '../../../types/adminBatch.ts';
import {batchSubmissionStatusColor, batchSubmissionStatusLabel, formatBatchDateTime} from '../../../utils/batchUi.ts';
import {SubmissionActions} from '../actions/SubmissionActions.tsx';
import {DefaultSubmissionTrace} from './DefaultSubmissionTrace.tsx';

interface SubmissionTableProps {
    submissions: AdminBatchSubmission[];
    jobId: number;
    jobType: AdminBatchJobType;
    totalSteps: number;
    onRecheck: (jobId: number, submissionId: number) => void;
    onCancel: (jobId: number, submissionId: number) => void;
    onNextProvider: (jobId: number, submissionId: number) => void;
    onRetryQuiz: (jobId: number, submissionId: number, executionMode: ExecutionMode) => void;
    onReprocessCached: (jobId: number, submissionId: number) => void;
    isPending: boolean;
}

const prettyJson = (value?: unknown): string => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value !== 'string') {
        try { return JSON.stringify(value, null, 2); } catch { return String(value); }
    }
    try { return JSON.stringify(JSON.parse(value), null, 2); } catch { return value; }
};

export const SubmissionTable: FC<SubmissionTableProps> = ({
    submissions, jobId, jobType,
    onRecheck, onCancel, onNextProvider, onRetryQuiz, onReprocessCached,
    isPending,
}) => {
    const [expandedIds, setExpandedIds] = useState<number[]>([]);
    const [copiedKey, setCopiedKey] = useState<string | null>(null);

    const copyText = async (key: string, text?: string) => {
        try {
            await navigator.clipboard.writeText(text ?? '');
            setCopiedKey(key);
            setTimeout(() => setCopiedKey((prev) => (prev === key ? null : prev)), 1200);
        } catch { setCopiedKey(null); }
    };

    const toggleExpand = (id: number) => {
        setExpandedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
        );
    };

    const renderTrace = (s: AdminBatchSubmission) => {
        return <DefaultSubmissionTrace submission={s} prettyJson={prettyJson} copyText={copyText} copiedKey={copiedKey} />;
    };

    return (
        <>
            <Alert severity="info" sx={{m: 1.5}}>
                성능을 위해 submission 이력은 페이지 단위로 조회합니다.
            </Alert>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell width={48}></TableCell>
                        <TableCell>Submission ID</TableCell>
                        <TableCell>청크</TableCell>
                        <TableCell>Provider</TableCell>
                        <TableCell>상태</TableCell>
                        <TableCell>재시도</TableCell>
                        <TableCell>요청/완료</TableCell>
                        <TableCell>오류</TableCell>
                        <TableCell align="center">작업</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {submissions.map((s) => {
                        const expanded = expandedIds.includes(s.id);
                        return (
                            <Fragment key={s.id}>
                                <TableRow>
                                    <TableCell>
                                        <IconButton size="small" onClick={() => toggleExpand(s.id)}>
                                            {expanded ? <KeyboardArrowDown fontSize="small"/> : <KeyboardArrowRight fontSize="small"/>}
                                        </IconButton>
                                    </TableCell>
                                    <TableCell>{s.id}</TableCell>
                                    <TableCell>{s.chunkIndex}</TableCell>
                                    <TableCell>
                                        {s.provider}
                                        {s.executionMode === 'STANDARD' && (
                                            <Chip label="Standard" size="small" color="info" sx={{ml: 0.5}}/>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip size="small" label={batchSubmissionStatusLabel(s.status)} color={batchSubmissionStatusColor(s.status)}/>
                                    </TableCell>
                                    <TableCell>{s.retryCount}</TableCell>
                                    <TableCell>
                                        <Typography variant="caption" display="block">{formatBatchDateTime(s.submittedAt)}</Typography>
                                        <Typography variant="caption" color="text.secondary">{formatBatchDateTime(s.completedAt)}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" color="text.secondary">
                                            {s.errorMessage || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <SubmissionActions
                                            submission={s} jobId={jobId} jobType={jobType}
                                            onRecheck={onRecheck} onCancel={onCancel}
                                            onNextProvider={onNextProvider} onRetryQuiz={onRetryQuiz}
                                            onReprocessCached={onReprocessCached}
                                            isPending={isPending}
                                        />
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell colSpan={9} sx={{py: 0}}>
                                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                                            {renderTrace(s)}
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                            </Fragment>
                        );
                    })}
                </TableBody>
            </Table>
        </>
    );
};
